import client from './client';
import type { PagedResult } from './anbiyam';

export interface CemeteryRecord {
  cemetery_id:      number;
  family_id?:       number | null;
  member_id?:       number | null;
  name:             string;
  gender?:          string;
  dob?:             string | null;
  grave_number?:    string | null;
  date_of_death?:   string | null;
  date_of_burial?:  string | null;
  notes?:           string | null;
  family_code?:     string | null;
  // Current year's cemetery subscription status — stored per FAMILY (not
  // per grave), so two records sharing one family (e.g. a couple on the
  // same grave) already share this automatically.
  subscription_status?: string | null;
  [key: string]: unknown;
}

export interface EligibleMember {
  member_id:    number;
  name:         string;
  relationship: string;
  gender?:      string;
  dob?:         string | null;
}

export interface CemeterySavePayload {
  cemeteryId?:   number;
  familyId:      number;
  memberId?:     number;
  graveNumber?:  string;
  burialPlace?:  string;
  dateOfDeath?:  string | null;
  dateOfBurial?: string | null;
  notes?:        string;
}

// NOTE: cemetery_subscription_yearly is keyed by family_id (a family-level
// yearly cemetery subscription), not by individual cemetery record.
export interface CemeterySubscriptionYear {
  family_id?:          number;
  subscription_year?:  number;
  amount?:             number;
  payment_date?:       string | null;
  payment_status?:     string;
  remarks?:            string | null;
  [key: string]: unknown;
}

export interface CemeterySubscriptionPayload {
  amount:        number;
  paymentDate:   string | null;
  paymentStatus: 'Paid' | 'Unpaid' | 'Pending';
  remarks?:      string | null;
}

export const listFamilyCemetery = async (familyId: number): Promise<CemeteryRecord[]> => {
  const { data } = await client.get<{ data: CemeteryRecord[] }>(`/family/${familyId}/cemetery`);
  return data.data;
};

export const listEligibleMembers = async (familyId: number): Promise<EligibleMember[]> => {
  const { data } = await client.get<{ data: EligibleMember[] }>(
    `/family/${familyId}/cemetery/eligible-members`,
  );
  return data.data;
};

export const saveCemeteryRecord = async (payload: CemeterySavePayload): Promise<CemeteryRecord> => {
  const { data } = await client.post<{ data: CemeteryRecord }>('/cemetery', payload);
  return data.data;
};

export const listCemeteries = async (params: {
  page?: number; pageSize?: number; search?: string; familyId?: number;
  /** true = on a real family, false = still on the CHURCH placeholder. */
  linked?: boolean;
  /** Inclusive YYYY-MM-DD bounds on date of burial. */
  burialFrom?: string; burialTo?: string;
}): Promise<PagedResult<CemeteryRecord>> => {
  const { data } = await client.get<{ data: PagedResult<CemeteryRecord> }>('/cemetery', { params });
  return data.data;
};

export const getCemeterySubscription = async (
  familyId: number, year: number,
): Promise<CemeterySubscriptionYear> => {
  const { data } = await client.get<{ data: CemeterySubscriptionYear }>(
    `/cemetery/family/${familyId}/subscriptions/${year}`,
  );
  return data.data;
};

export const deleteCemeteryRecord = async (cemeteryId: number): Promise<void> => {
  await client.delete(`/cemetery/${cemeteryId}`);
};

export const setCemeterySubscription = async (
  familyId: number, year: number, payload: CemeterySubscriptionPayload,
): Promise<CemeterySubscriptionYear> => {
  const { data } = await client.put<{ data: CemeterySubscriptionYear }>(
    `/cemetery/family/${familyId}/subscriptions/${year}`, payload,
  );
  return data.data;
};

export const deleteCemeterySubscription = async (
  familyId: number, year: number,
): Promise<void> => {
  await client.delete(`/cemetery/family/${familyId}/subscriptions/${year}`);
};

// ── Cemetery subscription list (individual graves × yearly status) ───────────
// One row per cemetery_details record (grave) — subscriptions are still
// keyed by family_id underneath (no per-grave subscription table), so two
// graves under the same family show the same amount/status/remarks.

export interface CemeterySubListRow {
  cemetery_id:                   number;
  grave_number:                  string | null;
  name:                          string;
  family_id:                     number;
  family_code:                   string;
  burial_date:                   string | null;
  // The grave's own Notes (cemetery_details.remarks) — distinct from
  // `remarks` below, which is the yearly subscription's own remarks field.
  grave_notes:                   string | null;
  cemetery_subscription_year_id: number | null;
  subscription_year:             number | null;
  amount:                        number | null;
  payment_date:                  string | null;
  payment_status:                string | null;
  remarks:                       string | null;
}

export interface CemeterySubListParams {
  year:        number;
  search?:     string;
  memberName?: string;
  anbiyam?:    number | null;
  page?:       number;
  pageSize?:   number;
}

export interface CemeterySubListResult {
  items:      CemeterySubListRow[];
  total:      number;
  totalPages: number;
  page:       number;
}

export const getCemeterySubscriptionList = async (
  params: CemeterySubListParams,
): Promise<CemeterySubListResult> => {
  const p: Record<string, string> = { year: String(params.year) };
  if (params.search)      p.search     = params.search;
  if (params.memberName)  p.memberName = params.memberName;
  if (params.anbiyam)     p.anbiyam    = String(params.anbiyam);
  if (params.page)     p.page     = String(params.page);
  if (params.pageSize) p.pageSize = String(params.pageSize);
  const { data } = await client.get<{ data: CemeterySubListResult }>(
    '/subscriptions/cemetery-list', { params: p },
  );
  return data.data;
};
